"use client"

import { MessageCircle, Edit3, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { inboxFloatingSearchFadeLayerClassName, inboxFloatingSearchFadeStyle } from "./inbox-floating-search-styles"

interface ChatInboxSkeletonProps {
  rows?: number
  className?: string
  layout?: "default" | "split"
  onComposeAction?: () => void
  onCloseAction?: () => void
}

export function ChatInboxSkeleton({
  rows = 8,
  className,
  layout = "default",
  onComposeAction,
  onCloseAction,
}: ChatInboxSkeletonProps) {
  const rowList = (
    <div className="divide-y">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="w-full p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="mb-2 flex items-center justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-10" />
              </div>
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  if (layout === "split") {
    return (
      <div className={cn("flex h-full min-h-0 flex-col", className)}>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="sticky top-0 z-30 isolate">
            <div aria-hidden style={inboxFloatingSearchFadeStyle} className={inboxFloatingSearchFadeLayerClassName} />
            <div className="relative z-10 px-4 pb-3 pt-3">
              <Skeleton className="h-12 w-full rounded-full" />
            </div>
          </div>
          {rowList}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="space-y-4 border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Messages</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="outline"
              className="h-8 w-8 rounded-full bg-transparent"
              onClick={() => {
                onComposeAction?.()
              }}
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Badge variant="secondary" className="rounded-full px-2 py-1 text-[10px]">
              Beta
            </Badge>
            <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => onCloseAction?.()} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Skeleton className="h-10 w-full rounded-full" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">{rowList}</div>
    </div>
  )
}

